import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUp, ArrowDown } from "untitledui-js/react";
import { useConfig } from "@hooks/useConfig";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { NavigationMenuProps, MenuLink } from "../types/NavigationTypes";
import { languageDutch } from "../i18n";
import { sanitizeUrl } from "../utils/sanitize";

export const NavigationMenu = ({
  className,
  isOpen,
  mainContentRef,
  navigation,
  onNavigate,
}: NavigationMenuProps & {
  navigation?: MenuLink[];
  onNavigate?: () => void;
}) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const langPrefix = currentLang === languageDutch ? "" : `${currentLang}/`;

  const [openSection, setOpenSection] = useState<string | null>(null);
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    const mainContentEl = mainContentRef?.current;

    if (mainContentEl) {
      if (isOpen) {
        mainContentEl.setAttribute("inert", "");
      } else {
        mainContentEl.removeAttribute("inert");
      }
    }

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, mainContentRef]);

  const { data, isLoading } = useConfig();

  // Select the header menu based on the current language
  const headerMenu = useMemo(() => {
    if (navigation) return navigation;
    return (
      currentLang === languageDutch
        ? data?.storyData?.content?.headerMenu
        : data?.storyData?.content?.headerMenuEN
    ) as MenuLink[];
  }, [data, navigation, currentLang]);

  // Reset open section when language changes to avoid mismatched state
  useEffect(() => {
    setOpenSection(null);
  }, [currentLang]);

  if (!navigation && isLoading) return null;

  const toggleSection = (section: string) =>
    setOpenSection(openSection === section ? null : section);

  const renderSubmenuLink = (
    submenuItem: NonNullable<MenuLink["submenu"]>[number]
  ) => {
    const classes = twMerge(
      "block text-white transition-colors hover:text-primary-500",
      location.pathname === `/${langPrefix}${submenuItem.link?.cached_url}`
        ? "text-primary-500"
        : ""
    );

    if (submenuItem?.link?.linktype === "url" && submenuItem.link.url) {
      return (
        <a
          href={sanitizeUrl(submenuItem.link.url)}
          target="_blank"
          rel="noopener noreferrer"
          className={classes}
          role="menuitem"
        >
          {submenuItem.Label}
        </a>
      );
    }

    return (
      <Link
        key={submenuItem._uid}
        to={`/${langPrefix}${submenuItem.link?.cached_url ?? "#"}`} // When there is no link, use # as we want to open the submenu in that case
        className={classes}
        role="menuitem"
        onClick={() => {
          onNavigate?.();
        }}
      >
        {submenuItem.Label}
      </Link>
    );
  };

  return (
    <nav className={className} aria-label="Main Navigation">
      <div
        className={twMerge(
          "pointer-events-none fixed inset-0 z-10 bg-black/15 backdrop-blur-xs transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0"
        )}
      />
      <aside
        className={twMerge(
          "fixed top-0 right-0 z-20 h-full w-full overflow-y-auto overscroll-contain bg-secondary-700 p-40 max-lg:px-24 max-md:px-8 lg:w-[80vw] xl:w-2/3 2xl:w-[57vw]",
          isOpen ? "translate-x-0" : "translate-x-full",
          // We don't add the transition on iOS devices.
          // Reason is because on iOS the navigation won't be displayed after opening it, clicking on the Okta button and then opening it again.
          // This is an issue that seems to have something to do with position fixed and transform.
          !isIos
            ? "transform transition-transform duration-300 ease-in-out"
            : ""
        )}
      >
        {isOpen && (
          <div className="flex flex-col lg:flex-row lg:space-x-10">
            <div className="space-y-4">
              {Array.isArray(headerMenu) &&
                headerMenu.map((menuItem: MenuLink) => {
                  const isActive = openSection === menuItem._uid;
                  let menuItemClass;
                  if (isActive) {
                    menuItemClass = "text-primary-500 lg:justify-between";
                  } else if (
                    openSection !== null &&
                    openSection !== menuItem._uid
                  ) {
                    menuItemClass = "text-white/70";
                  }

                  return (
                    <div key={menuItem._uid}>
                      <MenuLinkItem
                        langPrefix={langPrefix}
                        menuItem={menuItem}
                        menuItemClass={menuItemClass}
                        toggleSection={toggleSection}
                        onNavigate={onNavigate}
                      >
                        {menuItem.Label}
                        {(menuItem?.submenu?.length ?? 0) > 0 && (
                          <span className="ml-3 h-7 w-7">
                            {isActive ? (
                              <>
                                <ArrowUp
                                  size={28}
                                  className="block translate-px scale-[1.2] lg:hidden"
                                  strokeWidth={1.5}
                                />
                                <ArrowRight
                                  size={28}
                                  className="translate-pxs hidden scale-[1.2] lg:block"
                                />
                              </>
                            ) : (
                              <>
                                <ArrowDown
                                  size={28}
                                  className="block translate-px scale-[1.2] lg:hidden"
                                  strokeWidth={1.5}
                                />
                                <ArrowRight
                                  size={28}
                                  className="hidden translate-px scale-[1.2] lg:block"
                                />
                              </>
                            )}
                          </span>
                        )}
                      </MenuLinkItem>

                      {isActive && (menuItem?.submenu?.length ?? 0) > 0 && (
                        <ul className="animate-fade-in trim-both-cap-alphabetic my-6 space-y-2 border-l border-white/20 pl-6 text-xl lg:hidden">
                          {menuItem.submenu?.map((submenuItem) => (
                            <li key={submenuItem._uid}>
                              {renderSubmenuLink(submenuItem)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
            </div>

            <div className="w-full pt-6 lg:pt-0">
              {Array.isArray(headerMenu) &&
                headerMenu.map((menuItem: MenuLink) => {
                  return (
                    openSection === menuItem._uid &&
                    (menuItem?.submenu?.length ?? 0) > 0 && (
                      <ul
                        key={menuItem.Label}
                        className="animate-fade-in trim-both-cap-alphabetic hidden space-y-1 border-l border-white/20 pl-8 text-xl lg:block"
                      >
                        {menuItem.submenu?.map((submenuItem) => (
                          <li key={submenuItem._uid}>
                            {renderSubmenuLink(submenuItem)}
                          </li>
                        ))}
                      </ul>
                    )
                  );
                })}
            </div>
          </div>
        )}
      </aside>
    </nav>
  );
};

interface MenuLinkItemProps {
  langPrefix: string;
  menuItem: MenuLink;
  menuItemClass?: string;
  toggleSection: (section: string) => void;
  onNavigate?: () => void;
  children: React.ReactNode;
}

const MenuLinkItem = ({
  langPrefix,
  menuItem,
  menuItemClass,
  toggleSection,
  onNavigate,
  children,
}: MenuLinkItemProps) => {
  const classes = twMerge(
    "mr-24 flex w-full items-center text-left text-3xl font-semibold text-nowrap text-white transition-colors duration-300 ease-in-out hover:text-primary-500",
    menuItemClass
  );

  // If the menu item has an external URL, redirect the user in a new tab.
  if (menuItem?.link?.linktype === "url" && menuItem.link.url) {
    return (
      <a
        href={menuItem.link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
      >
        {children}
      </a>
    );
  }

  // For internal links, use the Link component and toggle the submenu if there is one.
  return (
    <Link
      to={`/${langPrefix}${menuItem.link?.cached_url ?? "#"}`}
      className={classes}
      onClick={(e) => {
        if (menuItem?.submenu?.length) {
          e.preventDefault();
          toggleSection(menuItem._uid);
        } else {
          onNavigate?.();
        }
      }}
    >
      {children}
    </Link>
  );
};
